package org.example.project.service

import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.example.project.manager.LumaticaApiManager
import org.example.project.enginetabular.loadWithTabularFilters
import org.example.project.utils.All

object CustomerApi {
    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = false
    }

    suspend fun loadAllDetails(): List<All> =
        decodeList(LumaticaApiManager.call("kundenverbrauch/details-all"))

    suspend fun loadDetails(firmenId: String): All? =
        decodeOne(LumaticaApiManager.call("kundenverbrauch/details/${apiPart(firmenId)}"))

    suspend fun saveDetails(firmenId: String, data: All): All? =
        decodeOne(
            LumaticaApiManager.call(
                "kundenverbrauch/details-save-json/${apiPart(firmenId)}/${apiPart(json.encodeToString(All.AllNames.serializer(), data))}"
            )
        )

    suspend fun loadVerbrauch(firmenId: String, monthKey: String): All? =
        decodeOne(LumaticaApiManager.call("kundenverbrauch/verbrauch/${apiPart(firmenId)}/${apiPart(monthKey)}"))

    suspend fun loadVerbrauchMonate(firmenId: String): List<All> =
        decodeList(LumaticaApiManager.call("kundenverbrauch/verbrauch-monate/${apiPart(firmenId)}"))

    suspend fun saveVerbrauchEntry(firmenId: String, monthKey: String, entryId: String, entry: All): All? =
        decodeOne(
            LumaticaApiManager.call(
                "kundenverbrauch/verbrauch-entry-save-json/${apiPart(firmenId)}/${apiPart(monthKey)}/${apiPart(entryId)}/${apiPart(json.encodeToString(All.AllNames.serializer(), entry))}"
            )
        )

    suspend fun deleteVerbrauchEntry(firmenId: String, monthKey: String, entryId: String): Boolean =
        decodeBool(
            LumaticaApiManager.call("kundenverbrauch/verbrauch-entry-delete/${apiPart(firmenId)}/${apiPart(monthKey)}/${apiPart(entryId)}")
        )

    suspend fun loadModules(firmenId: String): List<All> =
        decodeList(LumaticaApiManager.call("kundenverbrauch/customer-modules/${apiPart(firmenId)}/false"))

    suspend fun searchVerbrauchMonate(filters: Map<String, String>): List<All> =
        decodeList(loadWithTabularFilters("kundenverbrauch/cus-rechnung-monate-tabular", filters))

    suspend fun searchModules(filters: Map<String, String>): List<All> =
        decodeList(loadWithTabularFilters("kundenverbrauch/cus-modules-tabular", filters))

    suspend fun searchVerbrauch(monthKey: String, filters: Map<String, String>): List<All> =
        decodeList(
            loadWithTabularFilters(
                "kundenverbrauch/cus-verbrauch-tabular",
                filters,
                mapOf("monthKey" to monthKey)
            )
        )

    suspend fun searchVerbrauchDetails(monthKey: String, filters: Map<String, String>): List<All> =
        decodeList(
            loadWithTabularFilters(
                "kundenverbrauch/cus-verbrauch-details-tabular",
                filters,
                mapOf("monthKey" to monthKey)
            )
        )

    private fun decodeOne(raw: String): All? =
        runCatching { json.decodeFromString(All.AllNames.serializer(), raw) }.getOrNull()

    private fun decodeList(raw: String): List<All> =
        runCatching { json.decodeFromString(ListSerializer(All.AllNames.serializer()), raw) }.getOrDefault(emptyList())

    private fun decodeBool(raw: String): Boolean =
        raw.contains("\"ok\":true")

    private fun apiPart(value: String): String =
        js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String
}
